'use client'

import * as React from 'react'
import { useFeatureIsOn } from 'growthbook'
import { tw } from 'utils/classnames'
import { useHasStudioAiQuery } from '@repo/cf-user/hooks/use-user-data'
import { FEATURE_GROW_3265_HOMEPAGE_TRIAL_BAR } from '@web/utils/features'
import { TrialBar } from './trial-bar'

interface StickyTrialBarProps {
  title: string
  subtitle: string
  ctaLabel: string
  cancelLabel: string
  /** Id of the block the bar waits to clear before revealing itself. */
  revealAfterElementId: string
}

export function StickyTrialBar({
  title,
  subtitle,
  ctaLabel,
  cancelLabel,
  revealAfterElementId,
}: StickyTrialBarProps) {
  const isTrialBarOn = useFeatureIsOn(FEATURE_GROW_3265_HOMEPAGE_TRIAL_BAR)
  const { data: userHasStudioAi, isPending } = useHasStudioAiQuery()
  const barRef = React.useRef<HTMLDivElement>(null)
  const [isVisible, setIsVisible] = React.useState(false)

  React.useEffect(() => {
    // The homepage cannot use the `#enable-bottom-floating-banner` sentinel the other
    // floating banners observe: it sits below every block, and the page is too short for
    // it to ever scroll above the viewport, so the observer never fires.
    const update = () => {
      // Reveal as soon as that block is fully in view, rather than once it has cleared the
      // top: images above it load late and push it down the document, so a stricter test
      // keeps receding and lands the bar two sections lower than intended.
      // Falling back to one viewport keeps the bar reachable if the block ever goes away —
      // the sentinel above is a standing reminder that an unreachable trigger fails silently.
      const revealAfter = document.querySelector(`#${revealAfterElementId}`)
      const scrolledPastBlock = revealAfter
        ? revealAfter.getBoundingClientRect().bottom <= window.innerHeight
        : window.scrollY > window.innerHeight

      const distanceFromBottom =
        document.documentElement.scrollHeight - (window.scrollY + window.innerHeight)
      // Retreat one bar-height before the page bottom: the bar is fixed, so anything it
      // overlaps down there can never be scrolled clear of it — it would swallow the end
      // of the footer. Measuring rather than hardcoding keeps this right on mobile, where
      // the copy wraps and the bar is much taller.
      const wouldCoverPageEnd = distanceFromBottom <= (barRef.current?.offsetHeight ?? 0)

      setIsVisible(scrolledPastBlock && !wouldCoverPageEnd)
    }

    update()
    window.addEventListener('scroll', update, { passive: true })
    window.addEventListener('resize', update)
    return () => {
      window.removeEventListener('scroll', update)
      window.removeEventListener('resize', update)
    }
  }, [revealAfterElementId])

  if (!isTrialBarOn || isPending || userHasStudioAi) {
    return null
  }

  return (
    <div
      ref={barRef}
      className={tw(
        'fixed bottom-0 left-0 right-0 z-10 w-full transition-transform duration-300 ease-in-out',
        // `fixed-banner` is the marker TicketGenie looks for to lift the support bubble clear
        // of a bottom banner. Only while visible: unlike the other banners this one stays
        // mounted when hidden, and the widget measures whatever carries the class.
        isVisible ? 'fixed-banner translate-y-0' : 'translate-y-full',
      )}
    >
      <TrialBar title={title} subtitle={subtitle} ctaLabel={ctaLabel} cancelLabel={cancelLabel} />
    </div>
  )
}
